// Data corrections — the queue the team works to fix records.
//
// Two things feed it, shown together because from here they are the same
// thing: rules that spot a problem in the data, and colleagues reporting one
// they can see and a rule cannot. Anyone with crm.use can read the queue and
// report a record; applying or dismissing is crm.manage.
//
// Each row says what is wrong, why it was raised, and what would change —
// because the cost of a queue like this is trust. One confidently-wrong
// suggestion applied by a manager teaches everyone to ignore the rest, which
// is why the rules that ship here were measured against real prod rows and
// two of them were cut for being wrong more often than right (see
// api/src/services/data-quality.ts).

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Wrench, AlertTriangle, HelpCircle, Check, ExternalLink, ChevronRight } from 'lucide-react'

import { Modal, Button, Badge, EmptyState } from '@/components/ui'
import { useApiToken } from '@/auth/useApiToken'
import { useHasPermission } from '@/store/useStore'
import { api, ApiError, type DataSuggestion } from '@/api/client'
import { refreshDataset } from '@/store/refreshDataset'
import './MergeDuplicates.css'

const SEVERITY: Record<DataSuggestion['severity'], { label: string; tone: 'warn' | 'info' | 'neutral'; icon: JSX.Element }> = {
  fix: { label: 'Can be fixed here', tone: 'warn', icon: <Wrench size={13} /> },
  review: { label: 'Needs a look', tone: 'info', icon: <AlertTriangle size={13} /> },
  gap: { label: 'Missing information', tone: 'neutral', icon: <HelpCircle size={13} /> },
}

/** Severity order for the queue: things that can be fixed now, then things
 *  needing judgement, then gaps. Against real prod data the gap bucket is 75
 *  of 86 items — rendering it inline would bury every actionable one, so it
 *  collapses. */
const SEVERITY_ORDER: DataSuggestion['severity'][] = ['fix', 'review', 'gap']

const GROUP_TITLE: Record<DataSuggestion['severity'], string> = {
  fix: 'Ready to fix',
  review: 'Needs a decision',
  gap: 'Missing information',
}

/** A cleared value has to read as an action, not as an empty cell. */
const shownValue = (v: string | undefined) =>
  v === undefined ? '—' : v === '' ? '(empty)' : v

export function DataCorrectionsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const getToken = useApiToken()
  const canManage = useHasPermission('crm.manage')
  const [items, setItems] = useState<DataSuggestion[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [resolved, setResolved] = useState<string[]>([])

  const load = useCallback(async () => {
    setError(null)
    try {
      const token = await getToken()
      const { suggestions } = await api.dataSuggestions(token)
      setItems(suggestions)
    } catch (err) {
      setError(err instanceof ApiError && err.status === 403
        ? 'You do not have access to the corrections queue.'
        : err instanceof Error ? err.message : String(err))
    }
  }, [getToken])

  useEffect(() => {
    if (!open) return
    setItems(null); setResolved([]); setError(null)
    void load()
  }, [open, load])

  const act = async (s: DataSuggestion, action: 'apply' | 'dismiss') => {
    setBusy(s.key); setError(null)
    try {
      const token = await getToken()
      if (action === 'apply') {
        await api.applyDataSuggestion(token, s, s.suggestedValue ?? '')
        // The store still holds the old value — pull the change back so the
        // rest of the app agrees with what just happened.
        await refreshDataset()
      } else {
        await api.dismissDataSuggestion(token, s)
      }
      setResolved((r) => [...r, s.key])
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(null)
    }
  }

  const pending = useMemo(
    () => (items ?? []).filter((s) => !resolved.includes(s.key)),
    [items, resolved])

  const groups = useMemo(
    () => SEVERITY_ORDER
      .map((sev) => ({ sev, list: pending.filter((s) => s.severity === sev) }))
      .filter((g) => g.list.length > 0),
    [pending])

  // Gaps start closed: they are the long tail, and nothing in them can be
  // actioned from this screen anyway.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set(['gap']))
  const toggle = (sev: string) => setCollapsed((c) => {
    const next = new Set(c)
    if (next.has(sev)) next.delete(sev); else next.add(sev)
    return next
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={760}
      title="Data corrections"
      footer={<Button variant="default" onClick={onClose}>Close</Button>}
    >
      {error && (
        <div className="mdup-error" role="alert">
          <AlertTriangle size={14} /> <span>{error}</span>
        </div>
      )}

      {items === null && !error && <p className="mdup-status">Checking the contact records…</p>}

      {items !== null && pending.length === 0 && (
        <EmptyState
          icon={<Check size={32} />}
          title={resolved.length ? 'Queue cleared' : 'Nothing to correct'}
          hint={resolved.length
            ? `${resolved.length} ${resolved.length === 1 ? 'item' : 'items'} handled.`
            : 'No record currently trips one of the data checks, and nobody has reported a problem.'}
        />
      )}

      {pending.length > 0 && (
        <>
          <p className="mdup-intro">
            <Wrench size={13} />
            {pending.length} {pending.length === 1 ? 'item' : 'items'}. Anyone can report a
            problem from a contact page; applying a change needs admin rights.
            {!canManage && ' You can read the queue but not act on it.'}
          </p>
          {groups.map((g) => (
            <div key={g.sev} className="dq-group">
              <button
                type="button"
                className="dq-group-head"
                onClick={() => toggle(g.sev)}
                aria-expanded={!collapsed.has(g.sev)}
              >
                <ChevronRight size={14} className={collapsed.has(g.sev) ? '' : 'dq-chev--open'} />
                <span>{GROUP_TITLE[g.sev]}</span>
                <span className="dq-group-count">{g.list.length}</span>
              </button>
              {!collapsed.has(g.sev) && g.list.map((s) => {
            const sev = SEVERITY[s.severity]
            const canApply = canManage && s.suggestedValue !== undefined && s.field
            return (
              <section key={s.key} className="dq-item">
                <header className="dq-head">
                  <Badge tone={sev.tone} variant="soft">{sev.icon} {sev.label}</Badge>
                  <Link to={`/contacts/${s.entityId}`} className="dq-who" onClick={onClose}>
                    {s.entityLabel} <ExternalLink size={11} />
                  </Link>
                </header>
                <p className="dq-title">{s.title}</p>
                <p className="dq-detail">{s.detail}</p>
                {canApply && (
                  <p className="dq-change">
                    <code>{s.field}</code>
                    <span className="dq-from">{shownValue(s.currentValue)}</span>
                    <span aria-hidden>→</span>
                    <span className="dq-to">{shownValue(s.suggestedValue)}</span>
                  </p>
                )}
                {canManage && (
                  <div className="mdup-actions">
                    <Button variant="default" disabled={busy !== null} onClick={() => void act(s, 'dismiss')}>
                      {busy === s.key ? '…' : 'This is fine'}
                    </Button>
                    {canApply && (
                      <Button variant="primary" disabled={busy !== null} onClick={() => void act(s, 'apply')}>
                        {busy === s.key ? 'Applying…' : 'Apply change'}
                      </Button>
                    )}
                  </div>
                )}
              </section>
            )
              })}
            </div>
          ))}
        </>
      )}
    </Modal>
  )
}
